import { CANVAS_SIZE } from "@/lib/constants/slides";

import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import { resolveSlideTemplate } from "@ai-teach/types";
import { GrammarFormThumbnail } from "../editor/canvas/grammar/form/GrammarFormThumbnail";
import { GrammarResponseThumbnail } from "../editor/canvas/grammar/response/GrammarResponseThumbnail";
import { GrammarSentenceThumbnail } from "../editor/canvas/grammar/sentence/GrammarSentenceThumbnail";
import { HandWritingThumbnail } from "../editor/canvas/handWriting/HandWritingThumbnail";
import { FreeformSlideThumbnail } from "../editor/canvas/other/FreeformSlideThumbnail";
import { PhonicsDecodeThumbnail } from "../editor/canvas/phonics/decode/PhonicsDecodeThumbnail";
import { PhonicsEncodeThumbnail } from "../editor/canvas/phonics/encode/PhonicsEncodeThumbnail";
import { ReadingFluencyThumbnail } from "../editor/canvas/reading/fluency/ReadingFluencyThumbnail";
import { WordPronunciationThumbnail } from "../editor/canvas/word/pronunciation/WordPronunciationThumbnail";
import { WordVocabularyThumbnail } from "../editor/canvas/word/vocabulary/WordVocabularyThumbnail";

interface SlideThumbnailProps {
  slide: SlideFields;
  handleCardClick: () => void;
  isHidden?: boolean;
}

// Exhaustive by construction, mirroring the editor map: a new SlideTemplate needs a thumbnail.
const THUMBNAIL_BY_TEMPLATE: Record<SlideTemplate, React.ComponentType<{ slide: SlideFields }>> = {
  vocabulary: WordPronunciationThumbnail,
  listenRepeat: WordVocabularyThumbnail,
  freeformCanvas: FreeformSlideThumbnail,
  wordCanvas: FreeformSlideThumbnail,
  conversation: GrammarFormThumbnail,
  videoQuestion: GrammarResponseThumbnail,
  audioRevealPrompt: GrammarSentenceThumbnail,
  passageReading: ReadingFluencyThumbnail,
  soundToSpelling: PhonicsEncodeThumbnail,
  spellingToSound: PhonicsDecodeThumbnail,
  handWriting: HandWritingThumbnail,
};

/** The width every thumbnail's padding and type is authored for: one tile of the slide grid. */
const DESIGN_WIDTH = 200;
const DESIGN_HEIGHT = DESIGN_WIDTH / CANVAS_SIZE.freeform.aspectRatio;

export function SlideThumbnailBody({ slide, width }: { slide: SlideFields; width?: number }) {
  const template = resolveSlideTemplate(slide);
  const SlideComponent = template ? THUMBNAIL_BY_TEMPLATE[template] : FreeformSlideThumbnail;

  if (!width) return <SlideComponent slide={slide} />;

  return (
    <div
      className="origin-top-left"
      style={{
        width: DESIGN_WIDTH,
        height: DESIGN_HEIGHT,
        transform: `scale(${width / DESIGN_WIDTH})`,
      }}
    >
      <SlideComponent slide={slide} />
    </div>
  );
}

export function SlideThumbnail({ slide, isHidden, handleCardClick }: SlideThumbnailProps) {
  return (
    <div
      onClick={handleCardClick}
      className={`bg-surface ${CANVAS_SIZE.freeform.aspectRatioClass} rounded-xl overflow-hidden relative border-b-2 border-gray-300 dark:border-gray-800 ${
        isHidden ? "grayscale opacity-60" : ""
      }`}
    >
      <SlideThumbnailBody slide={slide} />
    </div>
  );
}
